import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom"
import { lazy, Suspense } from "react"

// Components
import { PostHogProvider } from "@/app/posthog-provider"
import { Background } from "@/components/Primitives/Background"
import { SmartNavigation } from "@/components/Complex/SmartNavigation"
import { PageFooter } from "@/components/Complex/PageFooter"

// Pages
import { QuestsPage } from "./pages/QuestsPage"
import { QuestDetailPage } from "./pages/QuestDetailPage"
import { MissionPage } from "./pages/MissionPage"
import { ProfilePage } from "./pages/ProfilePage"
import { LoginPage } from "./pages/LoginPage"
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage"
import { ForgotPasswordEmailPage } from "./pages/ForgotPasswordEmailPage"
import { ResetPasswordPage } from "./pages/ResetPasswordPage"
import { ResetPasswordSuccessPage } from "./pages/ResetPasswordSuccessPage"
import { SignupPage } from "./pages/SignupPage"
import { SignupEmailPage } from "./pages/SignupEmailPage"
import { SignupOnboardingPage } from "./pages/SignupOnboardingPage"
import { SignupProfilePage } from "./pages/SignupProfilePage"
import { CircleQuestionsPage } from "./pages/CircleQuestionsPage"
import { NotFoundPage } from "./pages/NotFoundPage"

// Hooks
import { loadToken } from "@/hooks/useLocalStorage"

// Lazy imports
import { UnsubscribePage } from "./pages/UnsubscribePage"
import { QuizPage } from "./pages/QuizPage"
const TestMarkdownPage = lazy(() =>
  import("./pages/TestMarkdownPage").then((m) => ({
    default: m.TestMarkdownPage,
  })),
)

export function AppRouter() {
  const loading = <div>Loading…</div>
  const token = loadToken()

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/quiz"
          element={
            <PostHogProvider
              apiKey={import.meta.env.VITE_POSTHOG_QUIZ_KEY as string}
            >
              <main className="flex-1">
                <QuizPage />
              </main>
            </PostHogProvider>
          }
        />

        <Route
          path="*"
          element={
            <PostHogProvider
              apiKey={import.meta.env.VITE_POSTHOG_KEY as string}
            >
              <Background />
              <SmartNavigation />
              <main className="flex-1 p-4 pt-2.5 mx-auto max-w-6xl min-h-dvh flex justify-center">
                <Routes>
                  {/* Core app routes */}
                  <Route
                    path="/quests/:questId"
                    element={<QuestDetailPage />}
                  />
                  <Route path="/quests" element={<QuestsPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route
                    path="/circle/:circleId/questions"
                    element={<CircleQuestionsPage />}
                  />
                  <Route
                    path="/missions/:missionId"
                    element={<MissionPage />}
                  />

                  {/* Authentication routes */}
                  <Route
                    path="/login/forgot/email"
                    element={<ForgotPasswordEmailPage />}
                  />
                  <Route
                    path="/login/forgot"
                    element={<ForgotPasswordPage />}
                  />
                  <Route
                    path="/login/reset/success"
                    element={<ResetPasswordSuccessPage />}
                  />
                  <Route path="/login/reset" element={<ResetPasswordPage />} />
                  <Route path="/login" element={<LoginPage />} />

                  {/* Signup routes */}
                  <Route path="/signup/email" element={<SignupEmailPage />} />
                  <Route
                    path="/signup/onboarding"
                    element={<SignupOnboardingPage />}
                  />
                  <Route
                    path="/signup/profile"
                    element={<SignupProfilePage />}
                  />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/unsubscribe" element={<UnsubscribePage />} />

                  {/* Default route */}
                  <Route
                    path="/"
                    element={
                      <Navigate to={token ? "/quests" : "/signup"} replace />
                    }
                  />
                  <Route
                    path="/test/markdown"
                    element={
                      <Suspense fallback={loading}>
                        <TestMarkdownPage />
                      </Suspense>
                    }
                  />

                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
                <PageFooter />
              </main>
            </PostHogProvider>
          }
        />
      </Routes>
    </BrowserRouter>
  )
}
